import { PhaserSlider } from "src/games/shared/TriSlider";
import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserGeneralGameObjectType } from "src/games/shared/phaserHelpers/HandyTypes";
import { PhaserColorWheel } from "src/games/shared/ColorWheel";
import ImageApi from 'src/utils/ImageHelper';
import { PhaserModalConfig, PhaserModal } from "src/games/shared/Modal";
import TranslateApi from "src/utils/TranslateHelper";
import { PhaserButtonConfig, PhaserButton } from "src/games/shared/Button";
import { Color } from "src/games/shared/ColorValue";
import { EndGameScene } from "src/games/PaintGame/scenes/EndGameScene";

// Paintbrush helper class to hold drawing attributes
class PaintBrush {
    public thickness: number;
    public color: number;
    public alpha: number;

    // default color yellow, thickness 5
    constructor() {
        this.thickness = 5;
        this.color = 0x223e93;
        this.alpha = 1;
    }
}

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    // Easel variables
    private easel: Phaser.GameObjects.Image;
    private easelControls: Phaser.GameObjects.Image;
    // Paintbrush holds draw stroke attributes
    private paintBrush: PaintBrush;
    private colorWheel: PhaserColorWheel;
    // Button attributes
    private rectButton: Phaser.GameObjects.Image;
    private ellipseButton: Phaser.GameObjects.Image;
    private lineButton: Phaser.GameObjects.Image;
    private screenshotButton: PhaserButton;
    private eraseButton: Phaser.GameObjects.Image;
    // Arrays to hold history and colors
    private lineHistory: Phaser.Geom.Point[];
    // Boolean variables for controlling drawing
    private makeLine: boolean;
    private makeRect: boolean;
    private makeEllipse: boolean;
    private erase: boolean;
    private drawShape: boolean;

    private easelGraphic: Phaser.GameObjects.Graphics;
    private easelTexture: Phaser.GameObjects.RenderTexture;
    // Shape starting position
    private startX: number;
    private startY: number;
    // Variables for slider
    private slider: PhaserSlider;
    private paintBrushCursor: string = 'url(' + require("/src/assets/qci/sprites/cursors/paint_brush.cur") + '), pointer';
    private ellipseCursor: string = 'url(' + require("/src/assets/qci/sprites/cursors/ellipse_cursor.cur") + '), pointer';
    private rectangleCursor: string = 'url(' + require("/src/assets/qci/sprites/cursors/rectangle_icon.cur") + '), pointer';
    private eraseCursor: string = 'url(' + require("/src/assets/qci/sprites/cursors/eraser_cursor.cur") + '), pointer';

    brushColor: number;
    brushThickness: number;

    public static CROPPED_IMAGE: string = null;

    constructor() {
        super({
            key: "MainScene"
        });
        bindAllOfThis(this, MainScene.prototype);
    }

    preload(): void {
        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.atlas("assets", require("/src/assets/qci/atlas/gamePaint.png"), require("src/assets/qci/atlas/gamePaint.json"));
        this.load.image("control-background", require("/src/assets/online-assets/sprites/astrology.png"));

        PhaserColorWheel.preload(this);
        PhaserSlider.preload(this);
    }

    create(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        var easelArea = new Phaser.Geom.Rectangle(0, 0, this.sys.canvas.width - 300, this.sys.canvas.height);
        var controlArea = new Phaser.Geom.Rectangle(easelArea.width, 0, 300, this.sys.canvas.height);

        this.paintBrush = new PaintBrush();
        this.brushColor = this.paintBrush.color;
        this.brushThickness = this.paintBrush.thickness;

        this.buildEasel(easelArea);
        this.buildControlArea(controlArea);
        this.buildControls(controlArea);

        this.lineHistory = [];
        this.makeLine = false;
        this.makeRect = false;
        this.makeEllipse = false;
        // Register the click events
        this.registerEvents();
        this.refreshPaintBrush();
        this.input.setDefaultCursor(this.paintBrushCursor);
        this.rectButton.setAlpha(0.5);
        this.ellipseButton.setAlpha(0.5);
        this.eraseButton.setAlpha(0.5);
    }

    buildEasel(easelArea: Phaser.Geom.Rectangle): void {
        this.easel = this.add.image(easelArea.x, easelArea.y, "pixel");
        this.easel.setOrigin(0, 0);
        this.easel.setDisplaySize(easelArea.width, easelArea.height);
        this.easel.setTint(0xffffff);
        this.easel.setDepth(-1);

        this.easelTexture = this.add.renderTexture(0, 0, this.sys.canvas.width - 300, this.sys.canvas.height);
        this.easelTexture.setOrigin(0);
        this.easelTexture.setDepth(0);

        this.easelGraphic = this.add.graphics();
        this.easelGraphic.setDepth(0);
    }

    buildControlArea(controlArea: Phaser.Geom.Rectangle) {
        this.easelControls = this.add.image(controlArea.x, controlArea.y, "control-background");
        this.easelControls.setOrigin(0, 0);
        this.easelControls.setDisplaySize(controlArea.width, controlArea.height);
        this.easelControls.setDepth(1);
    }

    buildControls(controlArea: Phaser.Geom.Rectangle): void {
        this.buildThicknessSlider(controlArea);
        this.buildLineButton();
        this.buildRectButton();
        this.buildEllipseButton();
        this.buildScreenshotButton(controlArea);
        this.buildEraseButton();
        this.buildColorWheel(controlArea);

        var iconsBackground = this.add.image(controlArea.centerX, 280, "pixel");
        iconsBackground.setDisplaySize(160, 160);
        iconsBackground.setDepth(1);

        Phaser.Actions.GridAlign([this.lineButton, this.rectButton, this.ellipseButton, this.eraseButton], {
            x: iconsBackground.x - iconsBackground.displayWidth / 4,
            y: iconsBackground.y - iconsBackground.displayHeight / 4,
            width: 2,
            height: 2,
            cellHeight: 80,
            cellWidth: 80
        });
    }

    refreshPaintBrush() {
        if (this.erase) {
            this.paintBrush.color = 0xffffff;
        }
        else {
            this.paintBrush.color = this.brushColor;
        }

        this.easelGraphic.lineStyle(this.paintBrush.thickness, this.paintBrush.color, this.paintBrush.alpha);
    }

    registerEvents(): void {
        // If pointer goes up outside the canvas, stop drawing
        document.addEventListener('mouseup', this.pointerUp);
        // When mouse click is pressed
        this.input.on('pointerdown', this.pointerDown);
        // When mouse click is released
        this.input.on('pointerup', this.pointerUp);
        // When mouse is moving after click is pressed
        this.input.on('pointermove', this.pointerMove);
        this.input.addPointer(5);
    }

    pointerDown(pointer: Phaser.Input.Pointer): void {
        if (this.slider.getIsSliding()) {
            return;
        } else if (this.makeRect) {
            this.startX = pointer.x;
            this.startY = pointer.y;
            this.drawShape = true;
        }
        else if (this.makeEllipse) {
            this.startX = pointer.x;
            this.startY = pointer.y;
            this.drawShape = true;
        }
        else {
            this.makeLine = true;
            this.lineHistory = [];
        }
    }

    pointerUp(): void {
        this.makeLine = false;
        this.drawShape = false;

        // Capture the graphics as a texture
        this.easelGraphic.generateTexture("background-drawn", this.sys.canvas.width - 300, this.sys.canvas.height);
        this.easelGraphic.clear();
        let tempImage: Phaser.GameObjects.Image = this.add.image(0, 0, "background-drawn");
        this.easelTexture.save();
        this.easelTexture.draw(tempImage.texture, tempImage.frame, 0, 0);
        this.easelTexture.restore();
        tempImage.destroy();
    }

    pointerMove(pointer: Phaser.Input.Pointer): void {
        // Show the shape being drawn as the mouse moves
        this.easelGraphic.clear();
        this.refreshPaintBrush();

        if (this.makeRect && this.drawShape) {
            // This fixes a strange issue the rectangle has at the corners when pulled a certain way
            let x1 = Math.min(this.startX, pointer.x);
            let x2 = Math.max(this.startX, pointer.x);
            let y1 = Math.min(this.startY, pointer.y);
            let y2 = Math.max(this.startY, pointer.y);
            let rect = new Phaser.Geom.Rectangle(x1, y1, x2 - x1, y2 - y1);
            this.easelGraphic.strokeRectShape(rect);
        } else if (this.makeEllipse && this.drawShape) {
            // This fixes an issue where ellipses are constructed from the center... Why?
            let ellipse = new Phaser.Geom.Ellipse(this.startX, this.startY, pointer.x - this.startX, pointer.y - this.startY);
            ellipse.left = this.startX;
            ellipse.top = this.startY;
            ellipse.right = pointer.x;
            ellipse.bottom = pointer.y;
            this.easelGraphic.strokeEllipseShape(ellipse, 1024);
        } else if (this.makeLine && !this.slider.getIsSliding()) {
            // Add the new points to the point history arrays
            this.lineHistory.push(new Phaser.Geom.Point(pointer.x, pointer.y));
            // Do the drawing
            this.easelGraphic.strokePoints(this.lineHistory);
        }
    }

    // Generate icon button for ellipse
    buildLineButton(): void {
        this.lineButton = this.add.image(0, 0, "assets", "line_icon");
        this.lineButton.setOrigin(0);
        this.lineButton.setDisplaySize(75, 75);
        this.lineButton.setDepth(2);
        this.lineButton.setInteractive();
        this.lineButton.on("pointerdown", () => {
            this.input.setDefaultCursor(this.paintBrushCursor);
            this.makeRect = false;
            this.makeEllipse = false;
            this.erase = false;
            this.makeLine = true;
            // Grey out other buttons
            this.lineButton.setAlpha(1);
            this.rectButton.setAlpha(0.5);
            this.ellipseButton.setAlpha(0.5);
            this.eraseButton.setAlpha(0.5);
        });
    }

    // Generate icon button for rectangles
    buildRectButton(): void {
        this.rectButton = this.add.image(0, 0, "assets", "rectangle_icon");
        this.rectButton.setDisplaySize(75, 75);
        this.rectButton.setOrigin(0);
        this.rectButton.setDepth(2);
        this.rectButton.setInteractive();
        this.rectButton.on("pointerdown", () => {
            this.input.setDefaultCursor(this.rectangleCursor);
            this.makeRect = true;
            this.makeEllipse = false;
            this.makeLine = false;
            this.erase = false;
            // Grey out other buttons
            this.rectButton.setAlpha(1);
            this.lineButton.setAlpha(0.5);
            this.ellipseButton.setAlpha(0.5);
            this.eraseButton.setAlpha(0.5);
        });
    }

    buildEraseButton() {
        this.eraseButton = this.add.image(0, 0, "assets", "erase_icon");
        this.eraseButton.setDisplaySize(75, 75);
        this.eraseButton.setOrigin(0);
        this.eraseButton.setDepth(2);
        this.eraseButton.setInteractive();
        this.eraseButton.on("pointerdown", () => {
            // Change to erasing
            this.input.setDefaultCursor(this.eraseCursor);
            this.makeRect = false;
            this.makeEllipse = false;
            this.makeLine = false;
            this.erase = true;
            // Grey out other buttons
            this.eraseButton.setAlpha(1);
            this.lineButton.setAlpha(0.5);
            this.ellipseButton.setAlpha(0.5);
            this.rectButton.setAlpha(0.5);
        });
    }

    // Generate icon button for ellipse
    buildEllipseButton(): void {
        this.ellipseButton = this.add.image(0, 0, "assets", "ellipse_icon");
        this.ellipseButton.setDisplaySize(75, 75);
        this.ellipseButton.setDepth(2);
        this.ellipseButton.setOrigin(0);
        this.ellipseButton.setInteractive();
        this.ellipseButton.on("pointerdown", () => {
            this.input.setDefaultCursor(this.ellipseCursor);
            this.makeRect = false;
            this.makeEllipse = true;
            this.makeLine = false;
            this.erase = false;
            // Grey out other buttons
            this.ellipseButton.setAlpha(1);
            this.rectButton.setAlpha(0.5);
            this.lineButton.setAlpha(0.5);
            this.eraseButton.setAlpha(0.5);
        });
    }

    // Generate icon button to screenshot the drawing
    buildScreenshotButton(controlArea: Phaser.Geom.Rectangle): void {
        var doneConfig: PhaserButtonConfig = {
            backgroundColor: Color.KUDER_PRIMARY_BUTTON,
            smartSize: true
        };

        this.screenshotButton = new PhaserButton(this, this.gameWidth - 200, 50, 200, 50, TranslateApi.translate("Paint.Phaser.thatsawrap"), doneConfig).setDepth(1);
        this.screenshotButton.setX(controlArea.x + controlArea.width / 2 - this.screenshotButton.width / 2);
        this.screenshotButton.on('buttonClicked', () => {
            // Grey out other buttons
            this.ellipseButton.setAlpha(0.5);
            this.rectButton.setAlpha(0.5);
            this.eraseButton.setAlpha(0.5);
            // Defaults back to line
            this.lineButton.setAlpha(1);
            this.makeRect = false;
            this.makeEllipse = false;
            this.makeLine = false;
            this.erase = false;

            // Screenshot for later
            this.registry.parent.renderer.snapshot((image: HTMLImageElement) => {
                image.onload = () => {
                    var croppedImage = ImageApi.CropImageToDataURI(image, 0, 0, this.sys.canvas.width - 300, this.sys.canvas.height);
                    console.warn("Screenshot!", croppedImage); // JB - Remove this console.warn

                    this.endGame(croppedImage);
                };
            });
        });
    }

    // Generate thickness slider
    buildThicknessSlider(controlArea: Phaser.Geom.Rectangle): void {
        this.slider = new PhaserSlider(this, controlArea.x + 22.5, 125, controlArea.height / 3, controlArea.width / 6);
        this.slider.setDepth(2);
        this.slider.on("slide", (ratio: number) => {
            this.paintBrush.thickness = Math.round(ratio * 20 + 5);
            this.brushThickness = Math.round(ratio * 20 + 5);
            this.refreshPaintBrush();
        });
    }

    buildColorWheel(controlArea: Phaser.Geom.Rectangle): void {
        this.colorWheel = new PhaserColorWheel(this, controlArea.x, controlArea.height - controlArea.width, controlArea.width, controlArea.width);
        this.refreshPaintBrush();

        this.colorWheel.on("colorChange", (color: Phaser.Display.Color) => {
            this.paintBrush.color = color.color;
            this.brushColor = color.color;
            this.refreshPaintBrush();
            if (this.makeEllipse) {
                this.input.setDefaultCursor(this.ellipseCursor);
            }
            else if (this.makeRect) {
                this.input.setDefaultCursor(this.rectangleCursor);
            }
            else if (this.erase) {
                this.input.setDefaultCursor(this.eraseCursor);
            }
            else {
                this.input.setDefaultCursor(this.paintBrushCursor);
            }
        });

        this.colorWheel.setDepth(2);
    }

    // Give the button attributes shared by all the buttons
    setupButton(rect: Phaser.Geom.Rectangle): Phaser.GameObjects.Image {
        let button = this.add.image(rect.x, rect.y, "pixel");
        button.setOrigin(0);
        button.setDepth(2);
        button.setInteractive();
        button.setDisplaySize(rect.width, rect.height);
        return button;
    }

    endGame(image: string): void {
        document.removeEventListener('mouseup', this.pointerUp);
        this.input.setDefaultCursor("default");
        this.input.removeAllListeners();
        MainScene.CROPPED_IMAGE = image;
        this.scene.add("EndGameScene", EndGameScene, true);
        this.scene.remove(this);
    }
}



// WEBPACK FOOTER //
// ./src/games/PaintGame/scenes/MainScene.ts